import { apiRequest } from "@/lib/api";

export type TurnRole = "student" | "patient" | "system";
export type TurnKind =
  | "anamnesis"
  | "physical_exam"
  | "test_request"
  | "test_result"
  | "decision"
  | "reasoning"
  | "system_note";

export interface EncounterTurnRead {
  id: string;
  turn_index: number;
  role: TurnRole;
  kind: TurnKind;
  content: string;
  meta: Record<string, unknown>;
  created_at: string;
}

export interface PatientInstanceRead {
  id: string;
  recipe_id: string;
  status: "active" | "closed" | "archived";
  simulated_time_offset_minutes: number;
  closed_at: string | null;
  next_event_at: string | null;
  last_evolution_at: string | null;
  evolution_count: number;
}

export interface ActivePatientSummary {
  encounter_id: string;
  patient_id: string;
  recipe_id: string;
  title: string;
  simulated_time_offset_minutes: number;
  next_event_at: string | null;
  last_evolution_at: string | null;
  evolution_count: number;
  requires_attention: boolean;
  last_event_description: string | null;
}

export interface ClinicalEventRead {
  id: string;
  simulated_time_minutes: number;
  event_type: string;
  source: string;
  description: string;
  meta: Record<string, unknown>;
  created_at: string;
}

export interface EncounterRead {
  id: string;
  patient_instance: PatientInstanceRead;
  status: "active" | "closed";
  closed_at: string | null;
  turns: EncounterTurnRead[];
  evaluation: Record<string, unknown>;
}

export interface CaseRecipeSummary {
  id: string;
  version: number;
  specialty: string;
  difficulty: number;
  title: string;
  teaching_objectives: string[];
  patient_summary: string;
}

export interface StartCaseResponse {
  encounter: EncounterRead;
}

export interface DecisionRead {
  id: string;
  kind: string;
  content: Record<string, unknown>;
  student_reasoning: string | null;
  evaluation: Record<string, unknown>;
  evaluated_at: string | null;
}

export interface ReasoningCompareRead {
  id: string;
  content: string;
  comparison_result: Record<string, unknown>;
  compared_at: string | null;
}

export interface CloseCaseResponse {
  encounter: EncounterRead;
  ideal_trace: Record<string, unknown>;
  student_comparison: Record<string, unknown> | null;
  differential_eval: Record<string, unknown> | null;
}

export const casesApi = {
  list: () => apiRequest<CaseRecipeSummary[]>("/cases"),

  listActive: () => apiRequest<ActivePatientSummary[]>("/cases/active"),

  start: (recipeId: string) =>
    apiRequest<StartCaseResponse>(`/cases/${recipeId}/start`, {
      method: "POST",
      body: {},
    }),

  getEncounter: (encounterId: string) =>
    apiRequest<EncounterRead>(`/cases/encounters/${encounterId}`),

  message: (encounterId: string, message: string) =>
    apiRequest<EncounterRead>(`/cases/encounters/${encounterId}/message`, {
      method: "POST",
      body: { message },
    }),

  physicalExam: (encounterId: string, command: string) =>
    apiRequest<EncounterRead>(
      `/cases/encounters/${encounterId}/physical-exam`,
      { method: "POST", body: { command } },
    ),

  orderTest: (encounterId: string, testKey: string) =>
    apiRequest<EncounterRead>(`/cases/encounters/${encounterId}/order-test`, {
      method: "POST",
      body: { test_key: testKey },
    }),

  testsAvailable: (encounterId: string) =>
    apiRequest<string[]>(
      `/cases/encounters/${encounterId}/tests-available`,
    ),

  submitDifferential: (
    encounterId: string,
    items: Array<{
      diagnostico: string;
      probabilidad?: string;
      justificacion?: string;
    }>,
    studentReasoning?: string,
  ) =>
    apiRequest<DecisionRead>(
      `/cases/encounters/${encounterId}/differential`,
      {
        method: "POST",
        body: { items, student_reasoning: studentReasoning },
      },
    ),

  submitReasoning: (encounterId: string, content: string) =>
    apiRequest<ReasoningCompareRead>(
      `/cases/encounters/${encounterId}/reasoning`,
      { method: "POST", body: { content } },
    ),

  close: (encounterId: string) =>
    apiRequest<CloseCaseResponse>(`/cases/encounters/${encounterId}/close`, {
      method: "POST",
      body: {},
    }),

  therapeutic: (
    encounterId: string,
    intervention: string,
    details?: string,
    studentReasoning?: string,
  ) =>
    apiRequest<DecisionRead>(`/cases/encounters/${encounterId}/therapeutic`, {
      method: "POST",
      body: {
        intervention,
        details,
        student_reasoning: studentReasoning,
      },
    }),

  forceAdvance: (encounterId: string, minutes: number) =>
    apiRequest<EncounterRead>(`/cases/encounters/${encounterId}/advance`, {
      method: "POST",
      body: { minutes },
    }),

  clinicalEvents: (encounterId: string) =>
    apiRequest<ClinicalEventRead[]>(
      `/cases/encounters/${encounterId}/clinical-events`,
    ),
};
